import {permiteRol} from '../../utils/permisosRol';
import {gestionarInvitaciones} from '../../utils/eventosService';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { prepararInvitaciones } from '../../utils/prepararInvitaciones';
import { compartirArchivosEntradasReserva } from '../../utils/compartirEntradasReserva';
import { useEventoVerificado } from '../../auth/EventPermission';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { db } from '../../firebase/firebaseConfig';
import { obtenerRelacionadoresEvento } from '../../utils/relacionadores';
import { etiquetaRelacionador } from '../../utils/identidadOperativa';
import { useAuth } from '../../context/AuthContext';
import { verificarAccesoEvento } from '../../utils/eventAccess';
import { doc } from 'firebase/firestore';
import { getDoc } from '../../utils/firestoreRead';
import VistaInvitacionEvento from './VistaInvitacionEvento';
import './GeneradorInvitaciones.css';

const normalizar = (texto) =>
  (texto || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}+/gu, '')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

export default function GenerarInvitacionesUnificado() {
  const navigate = useNavigate();
  const { eventoId } = useParams();
  const { usuario, profile } = useAuth();
  const verificado = useEventoVerificado();
  const esImagen=!permiteRol(profile||usuario,'invitaciones.ver');
  const [cupo,setCupo]=useState(null);
  const solicitud=useRef(null);

  const [validandoPermiso, setValidandoPermiso] = useState(true);
  const [accesoPermitido, setAccesoPermitido] = useState(false);

  const [evento, setEvento] = useState(null);

  const [lista, setLista] = useState('');
  const [atribuidoAUid, setAtribuidoAUid] = useState('');
  const [relacionadores, setRelacionadores] = useState([]);
  const atribuidoANombre = relacionadores.find((p) => p.id === atribuidoAUid)?.nombre || '';

  const [invitaciones, setInvitaciones] = useState([]);
  const [nuevasGeneradas, setNuevasGeneradas] = useState([]);
  const [duplicados, setDuplicados] = useState([]);
  const [viendoExistentes, setViendoExistentes] = useState(false);

  const [mensaje, setMensaje] = useState('');

  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');

  const textareaInvitadosRef = useRef(null);
  const gridRef = useRef(null);
  const [preparados, setPreparados] = useState(null);
  const [preparando, setPreparando] = useState(0);
  const [errorPreparacion, setErrorPreparacion] = useState('');
  const [reintento, setReintento] = useState(0);
  const [compartiendo, setCompartiendo] = useState(false);


  useEffect(() => {
    let alive = true;
    const validarPermiso = async () => {
      if (verificado?.eventoId === eventoId && verificado.permiso === 'invitaciones' && verificado.allowed) {
        setEvento(verificado.evento);
        setAccesoPermitido(true);
        setValidandoPermiso(false);
        return;
      }
      try {
        const resultado = await verificarAccesoEvento(eventoId, 'invitaciones');

        if (!resultado.permitido) {
          alert('No tienes acceso a este evento.');
          navigate('/dashboard/invitaciones');
          return;
        }

        if (!alive) return;
        setAccesoPermitido(true);
      } catch (error) {
        console.error('Error validando acceso al evento:', error);
        alert('No se pudo validar tu acceso al evento.');
        navigate('/dashboard/invitaciones');
      } finally {
        setValidandoPermiso(false);
      }
    };

    validarPermiso();
    return () => { alive = false; };
  }, [eventoId, navigate, verificado]);

  // --- Cargar evento ---
  useEffect(() => {
    if (!accesoPermitido) return;

    (async () => {
      if (!verificado?.evento) {
        const snap = await getDoc(doc(db, 'eventos', eventoId));
        if (snap.exists()) setEvento({ id: snap.id, ...snap.data() });
      }
      try{setCupo(await gestionarInvitaciones({accion:'estado',eventoId}));}catch(e){setMensaje(e.message);}
    })();
  }, [eventoId, accesoPermitido, verificado]);

  useEffect(() => {
    if (!accesoPermitido || !evento || esImagen) return;
    let alive = true;
    obtenerRelacionadoresEvento(evento, 'invitaciones').then((lista) => {
      if (alive) setRelacionadores(lista);
    }).catch(() => { if (alive) setRelacionadores([]); });
    return () => { alive = false; };
  }, [accesoPermitido, evento, esImagen]);

  // --- Lista de invitados normalizada ---
  const nombres = useMemo(() => {
    return (lista || '')
      .split('\n')
      .map((n) => ({ original: n.trim(), normalizado: normalizar(n) }))
      .filter((n) => n.original.length > 0);
  }, [lista]);

  const atribucionValida = !atribuidoAUid || relacionadores.some((p) => p.id === atribuidoAUid);

  const yaGenero = invitaciones && invitaciones.length > 0;

  const limpiarFormulario = () => {
    if (isGenerating) return;
    setAtribuidoAUid('');
    setLista('');
    setInvitaciones([]);
    setNuevasGeneradas([]);
    setDuplicados([]);
    setViendoExistentes(false);
    setMensaje('');
    setProgress(0);
    setProgressLabel('');
  };


  const limpiarTextoPegado = (texto) => {
    return String(texto || '')
      .replace(/\r/g, '')
      .split('\n')
      .map((linea) =>
        linea
          .replace(/^\s*>\s?/, '')
          .replace(/^\s*(?:[-*•]+|\d+[.)-])\s*/g, '')
          .replace(/\s+/g, ' ')
          .trim()
      )
      .filter(Boolean)
      .join('\n');
  };

  const pegarListaDesdePortapapeles = async () => {
    if (isGenerating || yaGenero) return;

    try {
      if (!navigator.clipboard?.readText) {
        setMensaje('⚠️ Tu navegador no permitió leer el portapapeles. Usa Ctrl + V dentro del cuadro.');
        textareaInvitadosRef.current?.focus();
        return;
      }

      const texto = await navigator.clipboard.readText();
      const limpio = limpiarTextoPegado(texto);

      if (!limpio) {
        setMensaje('⚠️ No encontré nombres válidos en el portapapeles.');
        textareaInvitadosRef.current?.focus();
        return;
      }

      setLista((actual) => {
        const base = String(actual || '').trim();
        return base ? `${base}\n${limpio}` : limpio;
      });
      setMensaje('✅ Lista pegada desde WhatsApp/portapapeles. Revisa antes de generar.');

      setTimeout(() => textareaInvitadosRef.current?.focus(), 80);
    } catch (error) {
      console.error('Error pegando desde portapapeles:', error);
      setMensaje('⚠️ No se pudo pegar automáticamente. Haz clic dentro del cuadro y usa Ctrl + V.');
      textareaInvitadosRef.current?.focus();
    }
  };

  const generarInvitaciones = async () => {
    if (isGenerating) return;

    setMensaje('');
    setDuplicados([]);
    setInvitaciones([]);
    setNuevasGeneradas([]);
    setViendoExistentes(false);
    setProgress(0);
    setProgressLabel('');

    if (!evento) {
      setMensaje('No se encontró el evento.');
      return;
    }

    if (!atribucionValida) {
      setMensaje(
        '⚠️ Si escribes un promotor, debe existir en la planilla oficial (RRPP/Imagen). O deja el campo vacío.'
      );
      return;
    }

    if (!Array.isArray(nombres) || nombres.length === 0) {
      setMensaje('⚠️ Agrega al menos 1 invitado en la lista.');
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setProgressLabel(`Generando 0/${nombres.length}...`);

    try {
      if(nombres.length>200)throw Error('Genera hasta 200 invitaciones por vez. Tu cupo total no cambia.');
      const payload={nombres:nombres.map(n=>n.original),atribuidoAUid:esImagen?null:atribuidoAUid||null};
      const key=JSON.stringify(payload);
      if(solicitud.current?.key!==key)solicitud.current={key,id:window.crypto?.randomUUID?.()||('invitacion-'+Date.now()+'-'+Math.random().toString(36).slice(2))};
      const result=await gestionarInvitaciones({accion:'emitir',eventoId,solicitudId:solicitud.current.id,...payload});
      const nuevas=result.nuevas,yaExisten=result.duplicados;
      solicitud.current=null;
      setCupo(c=>({...c,emitidas:result.emitidas,limite:result.limite,disponibles:result.limite===null?null:Math.max(0,result.limite-result.emitidas),invitaciones:[...(c?.invitaciones||[]),...nuevas]}));
      setNuevasGeneradas(nuevas);
      setInvitaciones(nuevas);
      setDuplicados(yaExisten);
      setViendoExistentes(false);

      setProgress(100);
      setProgressLabel(`Generando ${nombres.length}/${nombres.length}...`);

      if (yaExisten.length > 0 && nuevas.length === 0) {
        setMensaje(
          `⚠️ ${yaExisten.length} invitación(es) ya existían para este evento. No se crearon duplicados.`
        );
      } else if (yaExisten.length > 0 && nuevas.length > 0) {
        setMensaje(
          `⚠️ ${yaExisten.length} ya existían y no se duplicaron. ✅ ${nuevas.length} nuevas creadas.`
        );
      } else if (nuevas.length > 0) {
        setMensaje(`✅ Se generaron ${nuevas.length} invitaciones nuevas.`);
      } else {
        setMensaje('No se generaron invitaciones.');
      }
    } catch (e) {
      setMensaje(e.message||'No se pudieron generar las invitaciones. Puedes reintentar sin duplicarlas.');
    } finally {
      setIsGenerating(false);
    }
  };

  const fondo = useMemo(() => {
    if (!evento?.arteInvitacion) return null;
    return evento.arteInvitacion.startsWith('https://')||evento.arteInvitacion.startsWith('/eventos/') ? evento.arteInvitacion : `/eventos/${evento.arteInvitacion}`;
  }, [evento]);

  useEffect(() => {
    setPreparados(null);
    setErrorPreparacion('');
    setPreparando(0);
    if (!invitaciones.length || !evento) return;
    const controller = new AbortController();
    prepararInvitaciones(invitaciones, Array.from(gridRef.current?.querySelectorAll('.capture-area') || []), {
      signal: controller.signal,
      onProgress: count => { if (!controller.signal.aborted) setPreparando(count); },
    }).then(files => {
      if (!controller.signal.aborted) setPreparados({ items: invitaciones, evento, files });
    }).catch(error => {
      if (!controller.signal.aborted) setErrorPreparacion(error.message);
    });
    return () => controller.abort();
  }, [invitaciones, evento, reintento]);

  const archivosListos = preparados?.items === invitaciones && preparados?.evento === evento ? preparados.files : null;
  const descargarInvitaciones = async () => {
    if (!archivosListos || compartiendo) return;
    setCompartiendo(true);
    try {
      const zip = new JSZip();
      archivosListos.forEach(file => zip.file(file.name, file));
      saveAs(await zip.generateAsync({ type: 'blob' }), 'invitaciones.zip');
    } catch (error) { setMensaje('No se pudo descargar el ZIP. Intenta nuevamente.'); }
    finally { setCompartiendo(false); }
  };

  const shareTodasInvitaciones = async () => {
    if (!archivosListos || compartiendo) return;
    // La llamada nativa ocurre dentro del clic, antes de cualquier espera.
    const operacion = compartirArchivosEntradasReserva(archivosListos, 'invitaciones.zip');
    setCompartiendo(true);
    try {
      const resultado = await operacion;
      if (resultado === 'zip') setMensaje('Se descargó un ZIP con todas las invitaciones. Puedes adjuntarlo en WhatsApp.');
    } catch (error) { setMensaje('No se pudieron compartir las invitaciones. Intenta nuevamente o descarga el ZIP.'); }
    finally { setCompartiendo(false); }
  };

  // ✅ Ver existentes (sin descargar automático)
  const verExistentes = () => {
    if (!duplicados || duplicados.length === 0) return;

    setInvitaciones(duplicados);
    setViendoExistentes(true);

    setTimeout(() => {
      const grid = document.querySelector('.gi-grid');
      if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  // ✅ Ver nuevas (si hubo nuevas)
  const verNuevas = () => {
    if (!nuevasGeneradas || nuevasGeneradas.length === 0) return;

    setInvitaciones(nuevasGeneradas);
    setViendoExistentes(false);

    setTimeout(() => {
      const grid = document.querySelector('.gi-grid');
      if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  const disabledGenerar = isGenerating || !atribucionValida || !nombres || nombres.length === 0 || cupo?.permitido===false;

  if (validandoPermiso) {
    return <div style={{ padding: 30 }}>Validando permisos...</div>;
  }

  if (!accesoPermitido) {
    return null;
  }

  return (
    <div className="gi-pro">
      {isGenerating && (
        <div className="gi-overlay" aria-live="polite">
          <div className="gi-overlayCard">
            <div className="gi-overlayTitle">Generando invitaciones…</div>
            <div className="gi-overlaySub">
              {progressLabel} ({progress}%)
            </div>

            <div className="gi-progressBar">
              <div className="gi-progressFill" style={{ width: `${progress}%` }} />
            </div>

            <div className="gi-overlayHint">No cierres esta ventana hasta que termine.</div>
          </div>
        </div>
      )}

      <div className="gi-proHeader">
        <div>
          <h2 className="gi-proTitle">Generador de Invitaciones</h2>
          <div className="gi-proSubtitle">
            {evento?.nombre ? (
              <>
                Evento: <strong>{evento.nombre}</strong>
              </>
            ) : (
              'Cargando evento…'
            )}
          </div>
        </div>

        {evento && (
          <div className="gi-eventCard">
            <img src={fondo || ''} alt={evento.nombre} className="gi-eventThumb" />
            <div className="gi-eventMeta">
              <div>
                <strong>Fecha:</strong> {evento.fechaTexto || '-'}
              </div>
              <div>
                <strong>ID:</strong> {evento.id}
              </div>
            </div>
          </div>
        )}
      </div>

      {cupo && (
        <section className="gi-quota" aria-label="Cupo de invitaciones">
          <div className="gi-quotaHeader">
            <h3>Tus invitaciones</h3>
            <button type="button" className="gi-btn ghost" disabled={isGenerating || !cupo.invitaciones.length} onClick={()=>{setInvitaciones(cupo.invitaciones);setViendoExistentes(true);}}>
              {esImagen ? 'Invitaciones' : 'Ver invitaciones'}
            </button>
          </div>
          <dl className="gi-quotaGrid">
            <div className="gi-quotaMetric"><dt>Cupo por usuario</dt><dd>{cupo.limite === null ? 'Sin límite' : cupo.limite}</dd></div>
            <div className="gi-quotaMetric"><dt>Emitidas</dt><dd>{cupo.emitidas}</dd></div>
            <div className="gi-quotaMetric gi-quotaMetric--available"><dt>Disponibles</dt><dd>{cupo.disponibles === null ? 'Sin límite' : cupo.disponibles}</dd></div>
          </dl>
        </section>
      )}
      <div className="gi-steps">
        <div className={`gi-step ${yaGenero ? 'done' : 'active'}`}>
          <div className="gi-stepNum">1</div>
          <div className="gi-stepBody">
            <div className="gi-stepTitle">{esImagen?'Tu identidad':'Promotor (opcional)'}</div>
            <div className="gi-stepText">
              {esImagen?'Las invitaciones quedan vinculadas a tu cuenta.':'Selecciona un RRPP/Imagen o deja vacío si es para tus invitados.'}
            </div>

            {esImagen?<div className="gi-input">{profile?.nombre||usuario?.nombre||'Imagen'}</div>:<select aria-label="Relacionador de invitaciones" value={atribuidoAUid} onChange={(e) => setAtribuidoAUid(e.target.value)} disabled={isGenerating || yaGenero} className="gi-input">
              <option value="">Sin promotor / venta directa</option>
              {relacionadores.map((p) => <option key={p.id} value={p.id}>{etiquetaRelacionador(p)}</option>)}
            </select>}

            {atribuidoANombre && !atribucionValida && (
              <div className="gi-error">Ese promotor no existe en la planilla oficial.</div>
            )}

            {!esImagen&&relacionadores.length === 0 && (
              <div className="gi-help">
                No hay RRPP/Imágenes para mostrar. Revisa que existan usuarios con rol{' '}
                <strong>rrpp</strong> o <strong>imagen</strong> en Firestore (colección{' '}
                <strong>usuarios</strong>).
              </div>
            )}
          </div>
        </div>

        <div className={`gi-step ${yaGenero ? 'done' : 'active'}`}>
          <div className="gi-stepNum">2</div>
          <div className="gi-stepBody">
            <div className="gi-stepTitle">Invitados</div>

            <div className="gi-textareaToolbar">
              <button
                className="gi-btn gi-btn--paste"
                type="button"
                onClick={pegarListaDesdePortapapeles}
                disabled={isGenerating || yaGenero}
                title="Pegar lista copiada desde WhatsApp"
              >
                📋 Pegar
              </button>

              <span className="gi-toolbarHint"></span>
            </div>

            <textarea
              ref={textareaInvitadosRef}
              className="gi-textarea gi-textarea--pro"
              rows={10}
              value={lista}
              onChange={(e) => setLista(e.target.value)}
              disabled={isGenerating || yaGenero}
              placeholder={'Ej:\nJuan Perez\nMaria Gomez\n...'}
            />
            <div className="gi-small">
              Total: <strong>{nombres.length}</strong> invitado(s)
            </div>
          </div>
        </div>

        <div className={`gi-step ${yaGenero ? 'done' : 'active'}`}>
          <div className="gi-stepNum">3</div>
          <div className="gi-stepBody">
            <div className="gi-stepTitle">Generar</div>
            <div className="gi-stepText">
              Genera, espera el progreso y luego descarga o envía por WhatsApp.
            </div>

            {!yaGenero ? (
              <div className="gi-actionsPro">
                <button
                  className="gi-btn ghost"
                  type="button"
                  onClick={limpiarFormulario}
                  disabled={isGenerating}
                >
                  Limpiar
                </button>

                <button
                  className="gi-btn primary"
                  type="button"
                  onClick={generarInvitaciones}
                  disabled={disabledGenerar}
                >
                  Generar invitaciones
                </button>
              </div>
            ) : (
              <div className="gi-actionsPro">
                <button
                  className="gi-btn ghost"
                  type="button"
                  onClick={limpiarFormulario}
                  disabled={isGenerating}
                >
                  Nueva generación
                </button>

                <button
                  className="gi-btn secondary"
                  type="button"
                  onClick={descargarInvitaciones}
                  disabled={isGenerating || !archivosListos || compartiendo}
                >
                  Descargar ZIP
                </button>

                <button
                  className="gi-btn whatsapp"
                  type="button"
                  onClick={shareTodasInvitaciones}
                  disabled={isGenerating || !archivosListos || compartiendo}
                >
                  Enviar por WhatsApp
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {mensaje && (
        <div className="gi-resultCard">
          <div className="gi-resultTop">
            <div className="gi-resultMsg">{mensaje}</div>

            {duplicados?.length > 0 && (
              <div className="gi-resultActions">
                <button
                  className="gi-btn ghost"
                  type="button"
                  onClick={verExistentes}
                  disabled={isGenerating || viendoExistentes}
                >
                  Ver existentes
                </button>

                {nuevasGeneradas?.length > 0 && (
                  <button
                    className="gi-btn ghost"
                    type="button"
                    onClick={verNuevas}
                    disabled={isGenerating || !viendoExistentes}
                  >
                    Ver nuevas
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="gi-resultStats">
            <div className="gi-stat">
              <div className="gi-statLabel">Nuevas</div>
              <div className="gi-statValue">{nuevasGeneradas?.length || 0}</div>
            </div>
            <div className="gi-stat">
              <div className="gi-statLabel">Duplicadas</div>
              <div className="gi-statValue">{duplicados?.length || 0}</div>
            </div>
            <div className="gi-stat">
              <div className="gi-statLabel">Promotor</div>
              <div className="gi-statValue">{atribuidoANombre?.trim() ? atribuidoANombre : '—'}</div>
            </div>
          </div>
        </div>
      )}

      {invitaciones.length > 0 && !archivosListos && (
        <div className="gi-resultCard" role="status">
          {errorPreparacion ? <>{errorPreparacion} <button className="gi-btn ghost" onClick={() => setReintento(value => value + 1)}>Reintentar preparación</button></> : `Preparando imágenes: ${preparando}/${invitaciones.length}`}
        </div>
      )}
      <div className="gi-grid" ref={gridRef}>
        {invitaciones?.map((item, i) => (
          <div
            key={i}
            className="invitacion-preview preview-card"
            data-nombre={item?.nombre}
            data-atribuido-nombre={item?.atribuidoANombre}
          >
            <div className="capture-area">
              <VistaInvitacionEvento
                fondo={fondo || ''}
                codigo={item?.id}
                inline
                nombre={item?.nombre}
                atribuidoANombre={item?.atribuidoANombre}
                layoutInvitacion={evento?.layoutInvitacion}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
